import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild
} from "@angular/core";
import { take } from "rxjs/operators";
import { ConfirmModalService } from "@shared/services/confirm-modal.service";
import { AtlasModalService } from "@shared/services/atlas/modal/atlas-modal.service";
import { IAtlasButton } from "@shared/types/atlas/i-atlas-button";
import { IAtlasModal } from "@shared/types/atlas/i-atlas-modal";
import { Subject } from "rxjs";
import { YouTubePlayer } from "@angular/youtube-player";

interface IAtlasButtonModal extends IAtlasButton {
	click?: (e: Event, button: IAtlasButtonModal) => void;
	isConfirm?: boolean;
	isDecline?: boolean;
}

interface YoutubeStateChange {
	playerState: YT.PlayerState;
	currentTime: number;
}

@Component({
	selector: "app-atlas-modal",
	templateUrl: "./atlas-modal.component.html",
	styleUrls: ["./atlas-modal.component.scss"],
	standalone: false
})
export class AtlasModalComponent implements OnInit, OnDestroy {
	status = false;
	isUnsavedChangesModal = false;
	isHighRisk = false;
	isDisabledBtnDeleteHighRisk = false;
	title: string | null = null;
	body: string = "";
	bodyAligment: string = "center";
	bodyMuted: boolean = true;
	buttons: IAtlasButtonModal[] | null = null;
	header: IAtlasModal["header"] = undefined;
	size: IAtlasModal["size"] = "default";
	disableAutoClose: IAtlasModal["disable-auto-close"] = false;
	headerLogo: IAtlasModal["header-logo"] = undefined;
	headerImg: IAtlasModal["header-img"] = undefined;
	headerIllustration: IAtlasModal["header-illustration"] = undefined;
	headerImgDescription: IAtlasModal["header-img-description"] = undefined;
	skeletonLoading: IAtlasModal["skeleton-loading"] = null;
	youtubeSrc: IAtlasModal["youtubeSrc"] = undefined;
	youtubeScreenWidth: number = 0;
	youtubeScreenHeight: number = 0;

	youtubeStateChange: Subject<YoutubeStateChange> =
		new Subject<YoutubeStateChange>();

	@ViewChild("atlasModal")
	protected atlasModalRef!: ElementRef<IAtlasModal>;

	@ViewChild("player") player: YouTubePlayer = {
		getCurrentTime: () => 0
	} as YouTubePlayer;

	constructor(
		private atlasModalService: AtlasModalService,
		private confirmModalService: ConfirmModalService
	) {}

	loading$ = this.confirmModalService.status$;

	ngOnInit() {
		this.setYouTubePlayerSize();

		this.atlasModalService.onHidden
			.pipe(take(1))
			.subscribe(() =>
				this.confirmModalService.setNewStatus(this.status)
			);
	}

	ngOnDestroy() {
		this.onPlayerStateChange({ data: 0 } as YT.OnStateChangeEvent);
	}

	hide(): void {
		this.confirmModalService.setNewStatus(this.status);
		this.atlasModalRef.nativeElement?.closeModal?.();
	}

	confirm(): void {
		this.status = !this.isUnsavedChangesModal;
		this.hide();
	}

	decline(): void {
		this.status = this.isUnsavedChangesModal;
		this.hide();
	}

	changeStateBtnDeleteHighRisk(event: EventTarget | null): void {
		this.isDisabledBtnDeleteHighRisk = !(<HTMLInputElement>event).checked;
	}

	handleClick(event: Event, button: IAtlasButtonModal): void {
		if (button?.click) {
			return button.click(event, button);
		}

		if (button.isConfirm) {
			return this.confirm();
		}

		this.decline();
	}

	closeModal(): void {
		this.hide();
		this.atlasModalService.closeModal();
	}

	onPlayerStateChange(event: YT.OnStateChangeEvent): void {
		this.youtubeStateChange.next({
			playerState: event.data,
			currentTime: this.player?.getCurrentTime?.() ?? 0
		});
	}

	private setYouTubePlayerSize(): void {
		this.youtubeScreenWidth = Math.min(window.innerWidth * 0.85, 720);
		this.youtubeScreenHeight = 388;
	}
}
